import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../App";
import {
  shouldAcceptAdviceResponse,
  unavailableAdviceFallback,
} from "../lib/actionAdviceUi";
import { AdviceBlock, PolicyBlock } from "./ActionProposalPanels";
import { Btn, Card, Pill } from "./ui";

export default function ActionProposals() {
  const { session } = useAuth();
  const [doc, setDoc] = useState(null);
  const [adviceEnabled, setAdviceEnabled] = useState(false);
  const [selected, setSelected] = useState(null);
  const [advice, setAdvice] = useState(null);
  const [decision, setDecision] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const isAdmin = session?.user?.role === "admin";

  const refresh = async () => {
    const st = await api.actionProposalState();
    setDoc(st.document);
    setAdviceEnabled(Boolean(st.advice_enabled));
    const pending = (st.document?.proposals || []).filter((p) => p.status === "pending");
    setSelected(pending[pending.length - 1] || null);
  };

  useEffect(() => {
    refresh().catch((e) => setErr(e?.response?.data?.detail || "Could not load"));
  }, []);

  const adviceRequestId = useRef(0);

  useEffect(() => {
    // Hide previous advice immediately on selection / flag change.
    setAdvice(null);
    if (!adviceEnabled || !selected) {
      return undefined;
    }
    const requestId = ++adviceRequestId.current;
    const selectedSnap = selected;
    api
      .actionProposalAdvice({
        proposal_id: selectedSnap.id,
        revision: selectedSnap.revision,
        content_hash: selectedSnap.content_hash,
      })
      .then((out) => {
        if (
          shouldAcceptAdviceResponse({
            selected: selectedSnap,
            advice: out,
            requestId,
            activeRequestId: adviceRequestId.current,
          })
        ) {
          setAdvice(out);
        }
      })
      .catch(() => {
        const fb = unavailableAdviceFallback(selectedSnap);
        if (
          shouldAcceptAdviceResponse({
            selected: selectedSnap,
            advice: fb,
            requestId,
            activeRequestId: adviceRequestId.current,
          })
        ) {
          setAdvice(fb);
        }
      });
    return () => {
      // Bump so in-flight responses for this request are ignored.
      if (adviceRequestId.current === requestId) {
        adviceRequestId.current += 1;
      }
    };
  }, [adviceEnabled, selected?.id, selected?.revision, selected?.content_hash]);

  const run = async (fn) => {
    setBusy(true);
    setErr("");
    try {
      const out = await fn();
      if (out?.proposal && out?.label) {
        setDecision({
          proposalId: out.proposal.id,
          status: out.proposal.status,
          label: out.label,
          policy: out.policy,
        });
      }
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  const proposals = doc?.proposals || [];
  const decidedRow =
    decision &&
    proposals.find(
      (p) => p.id === decision.proposalId && p.status === decision.status
    );

  return (
    <div className="space-y-6" data-testid="action-proposals-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-white">Action proposals</h1>
        <p className="mt-1 max-w-2xl text-sm text-slate-400">
          Synthetic V0 workflow: prepare a customer reply, then approve or reject a
          <span className="text-slate-200"> send_email </span>
          proposal. Nothing is sent. Experimental human approval only.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Pill tone="muted">Experimenteel</Pill>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Btn
          data-testid="ap-create"
          disabled={busy}
          onClick={() => run(() => api.actionProposalCreate())}
        >
          Prepare customer reply
        </Btn>
        {isAdmin && (
          <Btn
            data-testid="ap-enable-send"
            disabled={busy}
            onClick={() =>
              run(() =>
                api.actionProposalPolicy({
                  allow_external_send: true,
                  allowed_recipients: ["klant@voorbeeld.nl"],
                })
              )
            }
          >
            Admin: allow send (supersedes pending)
          </Btn>
        )}
      </div>

      {err && (
        <div data-testid="ap-error" className="rounded-lg border border-block/40 bg-block/10 px-3 py-2 text-sm text-block">
          {err}
        </div>
      )}

      <Card>
        <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500">
          Policy epoch {doc?.policy_epoch ?? "—"} · doc version {doc?.doc_version ?? "—"} ·
          allow_external_send {String(doc?.bounds?.allow_external_send)}
        </div>
        {!selected ? (
          <p className="mt-3 text-sm text-slate-400">No pending proposal. Create one to review.</p>
        ) : (
          <div className="mt-4 space-y-3" data-testid="ap-pending">
            <div className="text-sm text-slate-300">
              <div><span className="text-slate-500">Tool:</span> {selected.tool_name}</div>
              <div><span className="text-slate-500">Revision:</span> {selected.revision}</div>
              <div><span className="text-slate-500">Recipient:</span> {selected.params.ontvanger}</div>
              <div><span className="text-slate-500">Subject:</span> {selected.params.onderwerp}</div>
              <div className="whitespace-pre-wrap">
                <span className="text-slate-500">Body:</span> {selected.params.body}
              </div>
            </div>
            <PolicyBlock policy={selected.policy_preview} testId="ap-pending-policy" />
            <AdviceBlock advice={advice} selected={selected} />
            <div className="flex flex-wrap gap-2">
              <Btn
                data-testid="ap-approve"
                disabled={busy}
                onClick={() =>
                  run(() =>
                    api.actionProposalApprove({
                      proposal_id: selected.id,
                      revision: selected.revision,
                      content_hash: selected.content_hash,
                    })
                  )
                }
              >
                Approve
              </Btn>
              <Btn
                data-testid="ap-reject"
                disabled={busy}
                onClick={() =>
                  run(() =>
                    api.actionProposalReject({
                      proposal_id: selected.id,
                      revision: selected.revision,
                      content_hash: selected.content_hash,
                    })
                  )
                }
              >
                Reject
              </Btn>
            </div>
          </div>
        )}
      </Card>

      {decidedRow && decision?.status === "approved_not_executed" && (
        <Card data-testid="ap-approved-card">
          <div className="text-sm font-semibold text-allow">{decision.label}</div>
          <div className="mt-1 font-mono text-[12px] text-slate-400">
            proposal {decision.proposalId} · rev {decidedRow.revision}
          </div>
          <div className="mt-3">
            <PolicyBlock policy={decision.policy} testId="ap-approved-policy" />
          </div>
        </Card>
      )}

      <Card>
        <h2 className="text-sm font-semibold text-white">
          Recent proposals ({proposals.length}/20)
        </h2>
        <ul className="mt-3 space-y-2" data-testid="ap-list">
          {[...proposals].reverse().map((p) => (
            <li key={p.id} className="font-mono text-[12px] text-slate-400">
              {p.status} · rev {p.revision} · {p.params?.onderwerp}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
